import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ImageList,
  ImageListItem,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  TrendingUp as EscalateIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
  Group as GroupIcon,
  Event as EventIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import UserActivityPanel from './UserActivityPanel';
import { updateDisputeStatus } from '../../../services/dispute';
import {
  getDisputeStatusConfig,
  DISPUTE_TYPE_LABELS,
  getDisputeReasonLabel,
  formatDisputeNumber,
  DISPUTE_STATUS,
  isDisputeResolved
} from '../../../constants/disputeConstants';
import { getCurrencySymbol } from '../../../utils/currencyUtils';

const DisputeReviewModal = ({ open, onClose, dispute, onSuccess }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [newStatus, setNewStatus] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  // User activity inline panel state
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null); // { id, name }
  const [activityType, setActivityType] = useState(null); // 0-3 or null

  useEffect(() => {
    if (open && dispute) {
      setNewStatus(dispute.status);
      setResolutionNote(dispute.resolutionNote || '');
      setError('');
    }
  }, [open, dispute]);

  if (!dispute) return null;

  const statusConfig = getDisputeStatusConfig(dispute.status);

  const handleSubmit = async () => {
    if (!resolutionNote.trim() && newStatus !== dispute.status) {
      setError('Please provide a resolution note for status change.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await updateDisputeStatus(dispute.id, {
        status: newStatus,
        resolutionNote: resolutionNote.trim()
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error updating dispute:', err);
      setError(err.response?.data?.message || 'Failed to update dispute. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    setNewStatus(action);
  };

  const handleUserClick = (event, partyId, partyName) => {
    setUserMenuAnchor(event.currentTarget);
    setSelectedParty({ id: partyId, name: partyName });
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleViewActivity = (tabIndex) => {
    setActivityType(tabIndex);
    handleUserMenuClose();
  };

  const handleCloseActivity = () => {
    setActivityType(null);
    setSelectedParty(null);
  };

  const showActivityPanel = selectedParty && activityType !== null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEvidenceFiles = () => {
    if (!dispute?.evidencePaths) return [];
    try {
      return dispute.evidencePaths.split(',').filter(path => path.trim());
    } catch {
      return [];
    }
  };

  const getFileType = (path) => {
    const extension = path.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'other';
  };

  const evidenceFiles = getEvidenceFiles();
  const hasStatusChanged = newStatus !== dispute.status;
  const canResolve = !isDisputeResolved(dispute.status);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={showActivityPanel ? 'xl' : 'lg'}
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon />
            <Typography variant="h6">Review Dispute</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left Column - Dispute Details */}
            <Grid item xs={12} md={showActivityPanel ? 5 : 8}>
              {/* Header Info */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {dispute.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<GavelIcon />}
                        label={formatDisputeNumber(dispute.disputeNumber)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={DISPUTE_TYPE_LABELS[dispute.disputeType]}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={getDisputeReasonLabel(dispute.reason)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Chip
                    label={statusConfig.label}
                    color={statusConfig.color}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Description */}
                <Paper sx={{ p: 2, bgcolor: 'action.hover', mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                    {dispute.description}
                  </Typography>
                </Paper>

                {/* Amount */}
                {dispute.disputedAmount && (
                  <Paper sx={{ p: 2, bgcolor: 'action.hover', mb: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Disputed Amount
                    </Typography>
                    <Typography variant="h6" color="error" fontWeight={600}>
                      {getCurrencySymbol(dispute.currency)} {parseFloat(dispute.disputedAmount).toLocaleString()}
                    </Typography>
                  </Paper>
                )}

                {/* Evidence */}
                {evidenceFiles.length > 0 && (
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Evidence ({evidenceFiles.length} files)
                    </Typography>
                    <ImageList cols={3} gap={8}>
                      {evidenceFiles.map((file, index) => {
                        const fileType = getFileType(file);
                        return (
                          <ImageListItem key={index}>
                            {fileType === 'image' ? (
                              <img
                                src={file}
                                alt={`Evidence ${index + 1}`}
                                loading="lazy"
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                  height: 100
                                }}
                                onClick={() => setSelectedImage(file)}
                              />
                            ) : fileType === 'pdf' ? (
                              <Box
                                sx={{
                                  height: 100,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  border: 1,
                                  borderColor: 'divider'
                                }}
                                onClick={() => window.open(file, '_blank')}
                              >
                                <PdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                                <Typography variant="caption">PDF</Typography>
                              </Box>
                            ) : null}
                          </ImageListItem>
                        );
                      })}
                    </ImageList>
                  </Paper>
                )}
              </Box>
            </Grid>

            {/* Right Column - Actions & Info */}
            <Grid item xs={12} md={showActivityPanel ? 3 : 4}>
              {/* Parties */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Parties Involved
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Filer
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      p: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={(e) => handleUserClick(e, dispute.filerId, dispute.filerName)}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500} color="primary" sx={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {dispute.filerName}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Respondent
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      p: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={(e) => handleUserClick(e, dispute.respondentId, dispute.respondentName)}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500} color="primary" sx={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {dispute.respondentName}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* User Activity Menu */}
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={() => handleViewActivity(0)}>
                  <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Transaction History</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleViewActivity(1)}>
                  <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Groups</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleViewActivity(2)}>
                  <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Events</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleViewActivity(3)}>
                  <ListItemIcon><CampaignIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Fundraisers</ListItemText>
                </MenuItem>
              </Menu>

              {/* Timeline */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Timeline
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {formatDate(dispute.createdAt)}
                </Typography>
                {dispute.resolvedAt && (
                  <>
                    <br />
                    <Typography variant="caption" color="success.main">
                      Resolved: {formatDate(dispute.resolvedAt)}
                    </Typography>
                  </>
                )}
              </Paper>

              {/* Quick Actions */}
              {canResolve && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      size="small"
                      variant={newStatus === 'UNDER_REVIEW' ? 'contained' : 'outlined'}
                      startIcon={<EscalateIcon />}
                      onClick={() => handleQuickAction('UNDER_REVIEW')}
                      fullWidth
                    >
                      Under Review
                    </Button>
                    <Button
                      size="small"
                      variant={newStatus === 'RESOLVED_FOR_FILER' ? 'contained' : 'outlined'}
                      startIcon={<CheckIcon />}
                      onClick={() => handleQuickAction('RESOLVED_FOR_FILER')}
                      color="success"
                      fullWidth
                    >
                      Resolve for Filer
                    </Button>
                    <Button
                      size="small"
                      variant={newStatus === 'RESOLVED_FOR_RESPONDENT' ? 'contained' : 'outlined'}
                      startIcon={<CheckIcon />}
                      onClick={() => handleQuickAction('RESOLVED_FOR_RESPONDENT')}
                      color="success"
                      fullWidth
                    >
                      Resolve for Respondent
                    </Button>
                    <Button
                      size="small"
                      variant={newStatus === 'RESOLVED_MUTUAL' ? 'contained' : 'outlined'}
                      startIcon={<CheckIcon />}
                      onClick={() => handleQuickAction('RESOLVED_MUTUAL')}
                      color="success"
                      fullWidth
                    >
                      Mutual Resolution
                    </Button>
                    <Button
                      size="small"
                      variant={newStatus === 'DISMISSED' ? 'contained' : 'outlined'}
                      startIcon={<BlockIcon />}
                      onClick={() => handleQuickAction('DISMISSED')}
                      color="error"
                      fullWidth
                    >
                      Dismiss
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Status Selection */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Update Status
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newStatus}
                    label="Status"
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={loading || isDisputeResolved(dispute.status)}
                  >
                    {Object.values(DISPUTE_STATUS).map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>

              {/* Resolution Note */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Resolution Note {hasStatusChanged && '*'}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Add notes about the resolution or status change..."
                  disabled={loading}
                  size="small"
                />
              </Paper>
            </Grid>

            {/* Activity Panel Column (shown when a user activity is selected) */}
            {showActivityPanel && (
              <Grid item xs={12} md={4}>
                <UserActivityPanel
                  userId={selectedParty.id}
                  userName={selectedParty.name}
                  activityType={activityType}
                  onClose={handleCloseActivity}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || (!hasStatusChanged && !resolutionNote.trim())}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Updating...' : 'Update Dispute'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Evidence Image
          <IconButton onClick={() => setSelectedImage(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Evidence"
              style={{ width: '100%', height: 'auto', borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>

    </>
  );
};

export default DisputeReviewModal;
